import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { ReactNode } from "react";

/**
 * The one-column rule: under 900px the sidebar has already become the tab bar,
 * so a screen showing columns should stop. Lives here because four modules had
 * byte-identical copies of it and a fifth was about to.
 */
const NARROW = "(max-width: 900px)";
export function useNarrow(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(NARROW);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(NARROW).matches,
    () => false,
  );
}

export function Card(p: { title?: ReactNode; right?: ReactNode; children: ReactNode; style?: React.CSSProperties; onClick?: () => void; ariaLabel?: string }) {
  const clickable = p.onClick
    ? {
        onClick: p.onClick,
        onKeyDown: (e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); p.onClick?.(); } },
        role: "link" as const,
        tabIndex: 0,
        "aria-label": p.ariaLabel,
        style: { cursor: "pointer" as const },
      }
    : { style: {} };
  return (
    <section {...(p.onClick ? { onClick: clickable.onClick, onKeyDown: clickable.onKeyDown, role: clickable.role, tabIndex: clickable.tabIndex, "aria-label": clickable["aria-label"] } : {})} style={{ background: "var(--card)", border: "1px solid var(--hairline)", borderRadius: "var(--r-card)", boxShadow: "var(--shadow)", padding: "16px 18px", ...clickable.style, ...p.style }}>
      {p.title != null && (
        <header style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <h3 style={{ fontSize: 14.5, fontWeight: 700 }}>{p.title}</h3>
          <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--ink-muted)" }}>{p.right}</div>
        </header>
      )}
      {p.children}
    </section>
  );
}

export function Button(
  p: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "danger" | "ghost" | "default";
    /** React 19 passes ref as a plain prop; Menu needs it to restore focus on Escape. */
    ref?: React.Ref<HTMLButtonElement>;
  },
) {
  const { variant = "default", style, ...rest } = p;
  const v: React.CSSProperties =
    variant === "primary" ? { background: "var(--accent)", color: "var(--accent-ink)", borderColor: "transparent" }
    : variant === "danger" ? { background: "var(--red-500)", color: "#fff", borderColor: "transparent" }
    : variant === "ghost" ? { background: "transparent" } : {};
  return (
    <button
      {...rest}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: "var(--r-ctl)", border: "1px solid var(--hairline)", background: "var(--card)", fontWeight: 600, fontSize: 13, color: "var(--ink-head)", ...v, ...(rest.disabled ? { opacity: 0.45, cursor: "not-allowed" } : {}), ...style }}
    />
  );
}

export function Pill(p: { kind: "ok" | "warn" | "bad" | "info" | "neutral"; children: ReactNode }) {
  const map = {
    ok: ["var(--ok-bg)", "var(--ok)", "✓"], warn: ["var(--warn-bg)", "var(--warn)", "⚠"],
    bad: ["var(--bad-bg)", "var(--bad)", "✕"], info: ["var(--info-bg)", "var(--info)", ""],
    neutral: ["var(--card-2)", "var(--ink-2)", ""],
  } as const;
  const [bg, fg, icon] = map[p.kind];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>
      {icon && <span aria-hidden>{icon}</span>}
      {p.children}
    </span>
  );
}

/* ------------------------------------------------------------ addresses */

/**
 * Keep one piece of screen state in the query string so the screen can be
 * linked to and reloaded: `?account=` on Customers, `?tab=` on Vendors and on
 * a vendor's page. `replaceState` rather than the router, because a tab switch
 * is not a navigation somebody should have to Back out of. Lived in the sales
 * module until T25 needed it in a second one.
 */
export function useQueryParam(key: string): [string | null, (v: string | null) => void] {
  const [value, setValue] = useState<string | null>(() => new URLSearchParams(location.search).get(key));
  const set = (v: string | null) => {
    setValue(v);
    const params = new URLSearchParams(location.search);
    if (v) params.set(key, v);
    else params.delete(key);
    const qs = params.toString();
    history.replaceState(null, "", qs ? `${location.pathname}?${qs}` : location.pathname);
  };
  return [value, set];
}

/* ------------------------------------------------------------------- forms */

export function Labeled({ label, help, children, wide }: { label: string; help?: string; children: ReactNode; wide?: boolean }) {
  return (
    <label style={{ display: "grid", gap: 4, minWidth: 0, gridColumn: wide ? "1 / -1" : undefined }}>
      <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ink-muted)", letterSpacing: ".03em", textTransform: "uppercase" }}>{label}</span>
      {children}
      {help && <span className="muted" style={{ fontSize: 11.5 }}>{help}</span>}
    </label>
  );
}

/* ------------------------------------------------------------------ status */

export type PillKind = "ok" | "warn" | "bad" | "info" | "neutral";
export interface StatusBadge { kind: PillKind; label: string; icon?: string }

/** Pill supplies its own glyph for ok, warn and bad; info and neutral bring one. */
export function Badge({ b, title }: { b: StatusBadge; title?: string }) {
  return (
    <span title={title}>
      <Pill kind={b.kind}>{b.icon ? <><span aria-hidden>{b.icon}</span>{b.label}</> : b.label}</Pill>
    </span>
  );
}

export function Chip({ icon, children, tone = "neutral", title }: { icon?: string; children: ReactNode; tone?: PillKind; title?: string }) {
  const [bg, fg] =
    tone === "warn" ? ["var(--warn-bg)", "var(--warn)"]
    : tone === "info" ? ["var(--info-bg)", "var(--info)"]
    : tone === "ok" ? ["var(--ok-bg)", "var(--ok)"]
    : tone === "bad" ? ["var(--bad-bg)", "var(--bad)"]
    : ["var(--card-2)", "var(--ink-2)"];
  return (
    <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>
      {icon && <span aria-hidden>{icon}</span>}{children}
    </span>
  );
}

/* ------------------------------------------------------------- error state */

/** What the API said went wrong, in the words it used. */
export function problemOf(e: unknown, fallback = "That did not save. Try again."): string {
  const body = (e as { body?: unknown } | null)?.body;
  if (typeof body === "string" && body.trim()) return body;
  if (body && typeof body === "object") {
    for (const k of ["detail", "message", "error"]) {
      const v = (body as Record<string, unknown>)[k];
      if (typeof v === "string" && v) return v;
    }
  }
  const msg = (e as { message?: unknown } | null)?.message;
  if (typeof msg === "string" && msg && !/^api \d+$/.test(msg)) return msg;
  return fallback;
}

export function Problem({ error, fallback }: { error: unknown; fallback?: string }) {
  if (!error) return null;
  return <p style={{ margin: "8px 0 0", color: "var(--bad)", fontSize: 12.5, fontWeight: 600 }}>⚠ {problemOf(error, fallback)}</p>;
}

export function Empty({ icon = "○", title, body }: { icon?: string; title: string; body?: string }) {
  return (
    <div style={{ display: "grid", gap: 4, padding: "18px 4px", justifyItems: "start" }}>
      <b style={{ color: "var(--ink-head)", fontSize: 13.5 }}><span aria-hidden style={{ marginRight: 6 }}>{icon}</span>{title}</b>
      {body && <span className="muted" style={{ fontSize: 12.5, maxWidth: "62ch" }}>{body}</span>}
    </div>
  );
}

/* ----------------------------------------------------------------- dialogs */

/** The dialogs open right now, top last. Only the top one answers Escape or a click on the backdrop. */
const OPEN_DIALOGS: symbol[] = [];
const FOCUSABLE = 'a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])';

/**
 * A skinned dialog. The browser dialogs are never used anywhere in the platform.
 *
 * Focus moves in on open (to `[data-autofocus]` if a child asks, else the
 * dialog itself), Tab wraps inside, and focus goes back to the opener on
 * close. Dialogs stack: a confirm opened from a detail closes on Escape
 * without taking the detail with it, which the old window listener did. A key
 * handler inside that has already handled Escape (a combobox closing its list)
 * marks the event and the dialog leaves it alone.
 */
export function Modal({
  open, title, hint, onClose, footer, children, width = 560,
}: {
  // Optional props take `| undefined` explicitly under exactOptionalPropertyTypes, because
  // passing an optional straight through, hint={p.hint}, is the ordinary React idiom.
  open: boolean; title: string; hint?: string | undefined; onClose: () => void;
  footer?: ReactNode; children: ReactNode; width?: number | undefined;
}) {
  const key = useRef<symbol | null>(null);
  if (key.current === null) key.current = Symbol("dialog");
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const me = key.current as symbol;
    OPEN_DIALOGS.push(me);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = dialogRef.current;
    if (root && !root.contains(document.activeElement)) {
      (root.querySelector<HTMLElement>("[data-autofocus]") ?? root).focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (OPEN_DIALOGS[OPEN_DIALOGS.length - 1] !== me) return;
      if (e.key === "Escape") {
        if (!e.defaultPrevented) closeRef.current();
        return;
      }
      if (e.key === "Tab" && root) {
        const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute("disabled"));
        const first = items[0];
        const last = items[items.length - 1];
        if (!first || !last) { e.preventDefault(); root.focus(); return; }
        const at = document.activeElement;
        if (e.shiftKey && (at === first || at === root)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = OPEN_DIALOGS.indexOf(me);
      if (i >= 0) OPEN_DIALOGS.splice(i, 1);
      opener?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  const onBackdrop = () => { if (OPEN_DIALOGS[OPEN_DIALOGS.length - 1] === key.current) onClose(); };
  return (
    <div
      role="presentation"
      onClick={onBackdrop}
      style={{ position: "fixed", inset: 0, zIndex: 60, padding: 16, display: "grid", placeItems: "center", background: "rgb(10 19 39 / .45)", overflowY: "auto" }}
    >
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} style={{ width: `min(${width}px, 100%)`, margin: "auto", outline: "none" }}>
        <Card title={title} right={<Button variant="ghost" onClick={onClose}>Close</Button>}>
          {hint && <p className="muted" style={{ margin: "0 0 12px", fontSize: 12.5 }}>{hint}</p>}
          <div style={{ display: "grid", gap: 12, maxHeight: "62vh", overflowY: "auto" }}>{children}</div>
          {footer && <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", marginTop: 14 }}>{footer}</div>}
        </Card>
      </div>
    </div>
  );
}

export interface Ask { title: string; body: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void }

/** Skinned confirmation, used for anything that leaves the building. */
export function useConfirm() {
  const [ask, setAsk] = useState<Ask | null>(null);
  const dialog = (
    <Modal
      open={ask !== null}
      title={ask?.title ?? ""}
      onClose={() => setAsk(null)}
      width={460}
      footer={
        <>
          <Button onClick={() => setAsk(null)}>Cancel</Button>
          <Button autoFocus variant={ask?.danger ? "danger" : "primary"} onClick={() => { ask?.onConfirm(); setAsk(null); }}>
            {ask?.confirmLabel}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 13.5 }}>{ask?.body}</p>
    </Modal>
  );
  return { confirm: (a: Ask) => setAsk(a), dialog };
}

/**
 * One onboarding checklist, with a progress bar and a row per item.
 *
 * Here rather than in a module because two screens draw it: HR runs everybody's
 * checklists and People shows a person their own, and modules may never import
 * each other. The row type is structural on purpose — `ChecklistItem` from
 * `@jaks/contract/hr` satisfies it — so this package keeps no dependency it
 * would otherwise not need.
 */
export interface ChecklistRow {
  id: string;
  label: string;
  owner: "hire" | "office";
  due: string;
  doneAt?: string;
  doneBy?: string;
}

export function Checklist({ items, onToggle, busy }: { items: readonly ChecklistRow[]; onToggle: (itemId: string, done: boolean) => void; busy: boolean }) {
  const done = items.filter((i) => i.doneAt).length;
  return (
    <>
      <div style={{ height: 8, background: "var(--card-2)", borderRadius: 999, border: "1px solid var(--hairline)", overflow: "hidden", margin: "0 0 12px" }}>
        <div style={{ width: `${items.length ? (done / items.length) * 100 : 0}%`, height: "100%", background: "var(--accent)" }} />
      </div>
      <div style={{ display: "grid" }}>
        {items.map((i) => (
          <label
            key={i.id}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 2px", borderTop: "1px solid var(--hairline)", cursor: busy ? "progress" : "pointer" }}
          >
            <input type="checkbox" checked={!!i.doneAt} disabled={busy} onChange={(e) => onToggle(i.id, e.target.checked)} style={{ width: 16, height: 16, flex: "0 0 auto", accentColor: "var(--accent)" }} />
            <span style={{ fontSize: 13, color: i.doneAt ? "var(--ink-muted)" : "var(--ink-head)", fontWeight: i.doneAt ? 400 : 600, textDecoration: i.doneAt ? "line-through" : "none" }}>
              {i.label}
            </span>
            <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
              {i.doneAt && <span className="muted" style={{ fontSize: 11.5 }}>{i.doneBy}</span>}
              <Pill kind="neutral">{i.owner === "hire" ? "Hire" : "Office"}</Pill>
              <Pill kind="neutral">{i.due}</Pill>
            </span>
          </label>
        ))}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ inputs */

/** `value`, settled: the last value that stayed put for `ms`. For a search box that asks the API as you type. */
export function useDebouncedValue<T>(value: T, ms = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/**
 * Segmented control: one of a few views. The shape the old portal header used; hoisted from the
 * Sales calendar in T23.
 *
 * `wrap` lets the options fall onto a second line instead of pushing the control wider than the
 * screen. Off by default, because a header control that reflows as the window narrows is worse
 * than one that stays put; on for Quick Range, whose four scope tiers are chosen by a thumb on a
 * 390 pixel phone and cannot fit on one line at a tap target's size.
 */
export function Segmented<T extends string>({ value, options, onChange, label, wrap }: {
  value: T; options: ReadonlyArray<{ id: T; label: string }>; onChange: (v: T) => void; label: string; wrap?: boolean;
}) {
  return (
    <span role="group" aria-label={label} style={{ display: "inline-flex", background: "var(--card-2)", border: "1px solid var(--hairline)", borderRadius: 9, padding: 2, ...(wrap ? { flexWrap: "wrap" } : {}) }}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button key={o.id} type="button" aria-pressed={on} onClick={() => onChange(o.id)}
            style={{ padding: "5px 12px", fontSize: 12.5, fontWeight: 600, borderRadius: 7, color: on ? "var(--ink-head)" : "var(--ink-muted)", background: on ? "var(--card)" : "transparent", border: on ? "1px solid var(--hairline)" : "1px solid transparent" }}>
            {o.label}
          </button>
        );
      })}
    </span>
  );
}

export interface SearchPickerProps<T> {
  /** Base for the ids ARIA needs; must be unique on the page. */
  id: string;
  label: string;
  help?: string;
  placeholder?: string;
  query: string;
  onQueryChange: (q: string) => void;
  options: readonly T[];
  loading?: boolean;
  error?: unknown;
  errorFallback?: string;
  value: T | null;
  onChange: (v: T | null) => void;
  getKey: (o: T) => string;
  renderOption: (o: T) => ReactNode;
  /** What the chip says once something is picked. */
  selectedLabel: (v: T) => ReactNode;
  /** The accessible name of the chip's remove button, e.g. "Remove account". */
  removeLabel: string;
  disabled?: boolean;
  emptyText?: string;
  minCharsText?: string;
  /** Span the whole form row, like `Labeled wide`. */
  wide?: boolean;
}

/**
 * A combobox that searches as you type and holds one pick as a chip.
 *
 * Data-agnostic on purpose: the caller runs the query and hands the options
 * in, so this package keeps no dependency on how a screen fetches. Keyboard:
 * the arrows move through the list and wrap, Home and End jump, Enter picks
 * only a row the arrows reached (never a row the person did not choose), the
 * first Escape closes the list, the second clears the box, and only the third
 * reaches a dialog around it. Tab closes without picking.
 */
export function SearchPicker<T>(p: SearchPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const removeRef = useRef<HTMLButtonElement>(null);
  const labelId = `${p.id}-label`;
  const listId = `${p.id}-listbox`;
  const helpId = `${p.id}-help`;
  const trimmed = p.query.trim();
  const listOpen = open && trimmed.length > 0 && !p.value;
  useEffect(() => { setActive(-1); }, [p.options]);
  const status = !trimmed
    ? (p.minCharsText ?? "Type to search.")
    : p.loading ? "Searching" : p.options.length === 0 ? (p.emptyText ?? "No matches.") : null;

  const choose = (o: T) => {
    p.onChange(o);
    p.onQueryChange("");
    setOpen(false);
    setActive(-1);
    requestAnimationFrame(() => removeRef.current?.focus());
  };
  const remove = () => {
    p.onChange(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const n = p.options.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      if (n) setActive((a) => (a + 1) % n);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      if (n) setActive((a) => (a <= 0 ? n - 1 : a - 1));
    } else if (e.key === "Home" && listOpen && n) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === "End" && listOpen && n) {
      e.preventDefault();
      setActive(n - 1);
    } else if (e.key === "Enter") {
      if (listOpen) e.preventDefault();
      const pick = active >= 0 ? p.options[active] : undefined;
      if (listOpen && pick !== undefined) choose(pick);
    } else if (e.key === "Escape") {
      if (listOpen) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setActive(-1);
      } else if (p.query) {
        e.preventDefault();
        e.stopPropagation();
        p.onQueryChange("");
      }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  const labelStyle: React.CSSProperties = { fontSize: 11.5, fontWeight: 700, color: "var(--ink-muted)", letterSpacing: ".03em", textTransform: "uppercase" };
  const inputStyle: React.CSSProperties = {
    padding: "7px 10px", height: 34, borderRadius: "var(--r-ctl)", border: "1px solid var(--hairline)",
    background: "var(--card)", color: "var(--ink)", fontFamily: "inherit", fontSize: 13, minWidth: 0, width: "100%",
  };
  return (
    <div style={{ display: "grid", gap: 4, minWidth: 0, gridColumn: p.wide ? "1 / -1" : undefined, position: "relative" }}>
      <span id={labelId} style={labelStyle}>{p.label}</span>
      {p.value ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: 34, padding: "4px 6px 4px 10px", borderRadius: "var(--r-ctl)", border: "1px solid var(--hairline)", background: "var(--card-2)", fontSize: 13, color: "var(--ink-head)" }}>
          <span style={{ minWidth: 0 }}>{p.selectedLabel(p.value)}</span>
          <button
            ref={removeRef}
            type="button"
            aria-label={p.removeLabel}
            disabled={p.disabled}
            onClick={remove}
            style={{ marginLeft: "auto", padding: "2px 8px", borderRadius: 999, fontSize: 14, lineHeight: 1, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--hairline)" }}
          >
            ×
          </button>
        </span>
      ) : (
        <>
          <input
            ref={inputRef}
            id={p.id}
            role="combobox"
            aria-labelledby={labelId}
            aria-describedby={p.help ? helpId : undefined}
            aria-autocomplete="list"
            aria-expanded={listOpen}
            aria-controls={listId}
            aria-activedescendant={listOpen && active >= 0 ? `${listId}-${active}` : undefined}
            autoComplete="off"
            disabled={p.disabled}
            placeholder={p.placeholder}
            value={p.query}
            onChange={(e) => { p.onQueryChange(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
            style={inputStyle}
          />
          {listOpen && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, marginTop: 4, background: "var(--card)", border: "1px solid var(--hairline)", borderRadius: "var(--r-ctl)", boxShadow: "var(--shadow)", maxHeight: 240, overflowY: "auto" }}>
              {status ? (
                <p role="status" aria-live="polite" className="muted" style={{ margin: 0, padding: "8px 10px", fontSize: 12.5 }}>{status}</p>
              ) : (
                <ul id={listId} role="listbox" aria-labelledby={labelId} style={{ margin: 0, padding: 4, listStyle: "none" }}>
                  {p.options.map((o, i) => (
                    <li
                      key={p.getKey(o)}
                      id={`${listId}-${i}`}
                      role="option"
                      aria-selected={i === active}
                      onMouseDown={(e) => e.preventDefault()}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => choose(o)}
                      style={{ padding: "7px 8px", borderRadius: 6, fontSize: 13, cursor: "pointer", background: i === active ? "var(--accent-soft)" : "transparent", color: "var(--ink-head)" }}
                    >
                      {p.renderOption(o)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      {p.help && <span id={helpId} className="muted" style={{ fontSize: 11.5 }}>{p.help}</span>}
      <Problem error={p.error} fallback={p.errorFallback ?? "The search did not answer."} />
    </div>
  );
}

/**
 * A button that opens a short list of actions (T36).
 *
 * The deal drawer had fourteen controls in one row, which is how a screen stops being used:
 * three of them ended the lead and the row gave no clue which one was wanted. This holds the
 * six a person needs occasionally, so the row can hold only the ones they need constantly.
 *
 * Keyboard: the arrows move through the items and wrap, Home and End jump, Escape closes and
 * returns focus to the button, Tab closes without choosing. A click outside closes it. The
 * items are buttons rather than links because every one of them opens something in place.
 */
export function Menu({ label, items, align = "left" }: {
  label: ReactNode;
  items: ReadonlyArray<{ id: string; label: string; onSelect: () => void; disabled?: boolean; group?: string }>;
  align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const hostRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const enabled = items.filter((i) => !i.disabled);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!hostRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function choose(id: string) {
    const item = items.find((i) => i.id === id);
    setOpen(false);
    buttonRef.current?.focus();
    item?.onSelect();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (e.key === "Tab") { setOpen(false); return; }
    if (enabled.length === 0) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Home" || e.key === "End") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
      const next = e.key === "Home" ? 0
        : e.key === "End" ? enabled.length - 1
        : (active + step + enabled.length) % enabled.length;
      setActive(next);
      return;
    }
    if ((e.key === "Enter" || e.key === " ") && active >= 0) {
      e.preventDefault();
      choose(enabled[active]!.id);
    }
  }

  let lastGroup: string | undefined;
  return (
    <span ref={hostRef} style={{ position: "relative", display: "inline-flex" }} onKeyDown={onKeyDown}>
      <Button
        ref={buttonRef}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { setOpen((o) => !o); setActive(-1); }}
      >
        {label} <span aria-hidden>▾</span>
      </Button>
      {open && (
        <div
          role="menu"
          aria-label={typeof label === "string" ? label : "More actions"}
          style={{
            position: "absolute", top: "calc(100% + 6px)", zIndex: 12, minWidth: 200,
            ...(align === "right" ? { right: 0 } : { left: 0 }),
            background: "var(--card)", border: "1px solid var(--hairline)", borderRadius: "var(--r-ctl)",
            boxShadow: "var(--shadow)", padding: 5, display: "grid", gap: 1,
          }}
        >
          {items.map((item) => {
            const idx = enabled.indexOf(item);
            const cap = item.group && item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <span key={item.id} style={{ display: "grid" }}>
                {cap && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "var(--ink-muted)", padding: "6px 10px 2px" }}>
                    {cap}
                  </span>
                )}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  tabIndex={-1}
                  onMouseEnter={() => idx >= 0 && setActive(idx)}
                  onClick={() => choose(item.id)}
                  style={{
                    textAlign: "left", padding: "7px 10px", borderRadius: 6, fontSize: 13, fontWeight: 500,
                    color: item.disabled ? "var(--ink-muted)" : "var(--ink)",
                    background: idx >= 0 && idx === active ? "var(--card-2)" : "transparent",
                    cursor: item.disabled ? "not-allowed" : "pointer",
                  }}
                >
                  {item.label}
                </button>
              </span>
            );
          })}
        </div>
      )}
    </span>
  );
}

export * from "./compose.tsx";
